/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useState } from 'react'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Flex,
  Heading,
  Select,
  Separator,
  Skeleton,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { TimeZoneSelect } from '~publish/components/TimeZoneSelect'
import {
  useAccount,
  useAccountTimezone,
} from '~publish/contexts/AccountContext'
import type {
  TimeFormat as TimeFormatEnum,
  StartOfWeek as StartOfWeekEnum,
} from '~publish/gql/graphql'
import { useDefaultScheduleOption } from '~publish/hooks/useDefaultScheduleOption'
import { useTimeFormatPreference } from '~publish/hooks/useTimeFormatPreference'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { SCHEDULE_OPTIONS } from '~publish/legacy/composer/composer/components/composer-footer/schedule-selector/constants'
import type { ScheduleOption } from '~publish/legacy/composer/interfaces/types'
import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

import { NewSettingsPageLayout } from './components/NewSettingsPageLayout'
import { useUpdateAccountPreferences } from './hooks/useUpdateAccountPreferences'
import { useUpdateTimeZonePreferences } from './hooks/useUpdateTimeZonePreferences'

import styles from './Settings.module.css'

const PreferenceDescription = ({
  id,
  heading,
  description,
}: {
  id: string
  heading: string
  description: string
}): JSX.Element => {
  return (
    <Flex
      gap="xs"
      align="baseline"
      direction="column"
      style={{ maxWidth: '440px' }}
    >
      <Heading size="small" id={`${id}-heading`} as="h3">
        {heading}
      </Heading>
      <Text id={`${id}-description`} color="subtle">
        {description}
      </Text>
    </Flex>
  )
}

const debugLog = getDebugLogger('UpdateAccountPreferences')

const genericSuccessMessage = 'Great! Your preferences have been saved'

const TimeZone = ({
  isLoading,
  timezone,
}: {
  isLoading: boolean
  timezone: string
}): JSX.Element => {
  const [localTimezone, setLocalTimezone] = useState(timezone)
  const {
    updateTimeZone,
    isLoading: isUpdatingTimeZone,
    hasError,
  } = useUpdateTimeZonePreferences()

  useEffect(() => {
    setLocalTimezone(timezone)
  }, [timezone])

  const handleOnChange = useCallback(
    (value: string): void => {
      if (value && value !== localTimezone) {
        setLocalTimezone(value)
        updateTimeZone({ timeZone: value }).then(() => {
          toast.success(genericSuccessMessage)
        })
      }
    },
    [updateTimeZone, localTimezone],
  )

  useEffect(() => {
    if (hasError) {
      setLocalTimezone(timezone) // Reset to previous value on error
      toast.error(
        'Sorry! There was a problem updating your selected timezone. Please try again.',
      )
    }
  }, [hasError, timezone])

  return (
    <Flex gap="xs" justify="between" fullWidth>
      <PreferenceDescription
        id="timezone"
        heading="Timezone"
        description="Used as the default timezone for new connected channels and for sending email notifications. Also used for calculating your posting streaks."
      />
      <Skeleton show={isLoading} data-testid="timezone-skeleton">
        <TimeZoneSelect
          variant="secondary"
          size="medium"
          onChange={handleOnChange}
          value={localTimezone}
          disabled={isUpdatingTimeZone}
          aria-labelledby="timezone-heading"
          aria-describedby="timezone-desc"
        />
      </Skeleton>
    </Flex>
  )
}

function assertTimeFormat(value: string): asserts value is TimeFormatEnum {
  if (!['twelveHour', 'twentyFourHour'].includes(value)) {
    throw new Error('Invalid time format')
  }
}

const TimeFormat = ({ isLoading }: { isLoading: boolean }): JSX.Element => {
  const timeFormat =
    useTimeFormatPreference() === '12' ? 'twelveHour' : 'twentyFourHour'
  const timeFormatOptions = [
    { label: '12-hour', value: 'twelveHour', timeFormat: '12' },
    { label: '24-hour', value: 'twentyFourHour', timeFormat: '24' },
  ]

  const selectedOption = timeFormatOptions.find(
    (option) => option.timeFormat === timeFormat,
  )

  const timeFormatLabel = selectedOption?.label || '12-hour'

  const [updateAccountPreferences, { loading, error }] =
    useUpdateAccountPreferences()

  const handleOnChange = useCallback(
    (value: string): void => {
      assertTimeFormat(value)
      updateAccountPreferences({
        variables: {
          input: {
            timeFormat: value,
          },
        },
      }).then(({ success }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your selected time format. Please try again.',
          )
        }
      })
    },
    [updateAccountPreferences, error],
  )

  return (
    <Flex gap="xs" justify="between" fullWidth>
      <PreferenceDescription
        id="time-format"
        heading="Time Format"
        description="Set the time format for the Calendar and Queue"
      />
      <Skeleton show={isLoading} data-testid="time-format-skeleton">
        <Select onValueChange={handleOnChange} value={timeFormat}>
          <Select.Trigger
            id="time-format"
            aria-labelledby="time-format-heading"
            aria-describedby="time-format-desc"
            placeholder={timeFormatLabel}
            disabled={loading}
            size="medium"
          />
          <Select.Content
            position="popper"
            align="end"
            sideOffset={0}
            className={styles.selectContent}
          >
            {timeFormatOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Skeleton>
    </Flex>
  )
}

function assertValidStartOfWeek(
  value: string,
): asserts value is StartOfWeekEnum {
  if (!['monday', 'sunday'].includes(value)) {
    throw new Error('Invalid start of week')
  }
}

const StartOfWeek = ({ isLoading }: { isLoading: boolean }): JSX.Element => {
  const startOfWeek = useWeekStartsOn()
  const [updateAccountPreferences, { loading, error }] =
    useUpdateAccountPreferences()

  const startOfWeekOptions = [
    { label: 'Monday', value: 'monday' },
    { label: 'Sunday', value: 'sunday' },
  ]

  const selectedOption = startOfWeekOptions.find(
    (option) => option.value === startOfWeek,
  )
  const startOfWeekLabel = selectedOption?.label || 'Monday'

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidStartOfWeek(value)
      updateAccountPreferences({
        variables: {
          input: {
            startOfWeek: value,
          },
        },
      }).then(({ success }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your selected start of week. Please try again.',
          )
        }
      })
    },
    [updateAccountPreferences, error],
  )

  return (
    <Flex gap="xs" justify="between" fullWidth>
      <PreferenceDescription
        id="start-of-week"
        heading="Start of Week"
        description="Set the first day of the week for the Calendar, date picker, and your posting streaks."
      />
      <Skeleton show={isLoading} data-testid="start-of-week-skeleton">
        <Select onValueChange={handleOnChange} value={startOfWeek}>
          <Select.Trigger
            id="start-of-week"
            aria-labelledby="start-of-week-heading"
            aria-describedby="start-of-week-desc"
            placeholder={startOfWeekLabel}
            disabled={loading}
            size="medium"
          />
          <Select.Content
            position="popper"
            align="end"
            sideOffset={0}
            className={styles.selectContent}
          >
            {startOfWeekOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Skeleton>
    </Flex>
  )
}

const ScheduleOptionKeys: ScheduleOption[] = [
  'Queue',
  'Now',
  'FixedTime',
  'Prioritize',
]

function assertValidOption(value: string): asserts value is ScheduleOption {
  if (!ScheduleOptionKeys.some((option) => value === option)) {
    throw new Error('Invalid schedule option')
  }
}

const DefaultScheduleOption = ({
  isLoading,
}: {
  isLoading: boolean
}): JSX.Element => {
  const defaultScheduleOption = useDefaultScheduleOption()
  const [updateAccountPreferences, { loading }] = useUpdateAccountPreferences()

  const handleOnChange = useCallback(
    (value: string): void => {
      assertValidOption(value)
      updateAccountPreferences({
        variables: {
          input: {
            defaultScheduleOption: value,
          },
        },
      }).then(({ success, error }) => {
        if (success) {
          toast.success(genericSuccessMessage)
        } else {
          debugLog(error)
          toast.error(
            'Sorry! There was a problem updating your default posting action. Please try again.',
          )
        }
      })
    },
    [updateAccountPreferences],
  )

  return (
    <Flex gap="xs" justify="between" fullWidth>
      <PreferenceDescription
        id="default-schedule-option"
        heading="Default Posting Action"
        description="Set a default time to post for the composer. We will use it whenever the composer is triggered without a specific time."
      />
      <Skeleton show={isLoading} data-testid="default-schedule-option-skeleton">
        <Select onValueChange={handleOnChange} value={defaultScheduleOption}>
          <Select.Trigger
            id="default-schedule-option"
            placeholder={SCHEDULE_OPTIONS[defaultScheduleOption].label}
            disabled={loading}
            size="medium"
          />
          <Select.Content
            position="popper"
            align="end"
            sideOffset={0}
            className={styles.selectContent}
          >
            {ScheduleOptionKeys.map((option) => (
              <Select.Item key={option} value={option}>
                {SCHEDULE_OPTIONS[option].label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Skeleton>
    </Flex>
  )
}

const Preferences = (): JSX.Element => {
  const hasNewScheduleSelector = useIsSplitEnabled(
    'geid-new-scheduler-selector-in-composer',
  )

  const { loading } = useAccount()
  const timezone = useAccountTimezone()

  return (
    <NewSettingsPageLayout
      title="Preferences"
      data-testid="email-notifications-skeleton"
    >
      <TimeZone isLoading={loading} timezone={timezone} />
      <Separator />
      <TimeFormat isLoading={loading} />
      <Separator />
      <StartOfWeek isLoading={loading} />

      {hasNewScheduleSelector && (
        <>
          <Separator />
          <DefaultScheduleOption isLoading={loading} />
        </>
      )}
    </NewSettingsPageLayout>
  )
}

export default Preferences
